import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/Pagination';
import { cn } from '@/lib/utils';
import { ArrowUpDown, BadgeCheck, Clock, Pencil } from 'lucide-react';
import { TeamBadge } from '@/components/ui/TeamBadge';

/** Row model for GET /api/users/ list (players page) */
export interface Player {
  user_id: string;
  user_full_name: string;
  user_email: string;
  user_avatar: string;
  team: string;
  team_color_code?: string;
  is_active: boolean;
  is_verified: boolean;
  last_active: string;
  date_joined: string;
  rank: number;
}

type SortField = 'name' | 'last_active' | 'joined';
type SortDirection = 'asc' | 'desc';

interface PlayerTableProps {
  data?: Player[];
  totalCount?: number;
  totalPages?: number;
  currentPage?: number;
  onNext?: () => void;
  onPrev?: () => void;
  onFirst?: () => void;
  onLast?: () => void;
  onSort?: (field: SortField, direction: SortDirection) => void;
}

export function PlayerTable({
  data = [],
  totalCount,
  totalPages = 1,
  currentPage = 1,
  onNext,
  onPrev,
  onFirst,
  onLast,
  onSort,
}: PlayerTableProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    onSort?.(field, newDirection);
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead
      className={cn(
        'h-11 cursor-pointer select-none px-4 transition-colors',
        'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
      )}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown
          className={cn(
            'ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/50',
            sortField === field && 'text-primary'
          )}
        />
        {sortField === field && (
          <span className="text-xs font-medium text-primary tabular-nums">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </TableHead>
  );

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const getTimeAgo = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatJoined = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const teamColorKey = (team: string) => team.toLowerCase().split(' ')[0] || '';

  const shownTotal = totalCount ?? data.length;

  return (
    <div className="space-y-0">
      <Table className="min-w-[720px]">
          <TableHeader className="sticky top-0 z-[1] border-b border-border/80 bg-muted/50 backdrop-blur-sm [&_tr]:border-b-0">
            <TableRow className="border-0 hover:bg-transparent">
              <TableHead className="h-11 w-12 px-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                #
              </TableHead>
              <SortableHeader field="name">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Player
                </span>
              </SortableHeader>
              <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Team
              </TableHead>
              <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Status
              </TableHead>
              <SortableHeader field="last_active">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Last active
                </span>
              </SortableHeader>
              <SortableHeader field="joined">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Joined
                </span>
              </SortableHeader>
              <TableHead className="h-11 px-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="[&_tr:last-child]:border-b-0">
            {data.length === 0 ? (
              <TableRow className="border-0 hover:bg-transparent">
                <TableCell colSpan={7} className="h-32 text-center text-sm text-muted-foreground">
                  No players on this page.
                </TableCell>
              </TableRow>
            ) : null}
            {data.map((player, rowIdx) => {
              const tc = teamColorKey(player.team);

              return (
                <TableRow
                  key={player.user_id}
                  className={cn(
                    'border-b border-border/50 transition-colors',
                    rowIdx % 2 === 1 ? 'bg-muted/15' : 'bg-card',
                    'hover:bg-primary/[0.04] dark:hover:bg-primary/10'
                  )}
                >
                  <TableCell className="w-12 px-3 py-3 text-center text-xs tabular-nums text-muted-foreground">
                    {player.rank}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <Link
                      to={`/players/${player.user_id}`}
                      className="flex items-center gap-3 rounded-md -m-1 p-1 transition-colors hover:bg-muted/50"
                    >
                      <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/80 bg-primary/10 shadow-sm">
                        {player.user_avatar ? (
                          <img
                            src={player.user_avatar}
                            alt={player.user_full_name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-primary">
                            {getInitials(player.user_full_name || 'U')}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <p className="max-w-[200px] truncate text-sm font-medium text-foreground">
                            {player.user_full_name}
                          </p>
                          {player.is_verified && (
                            <BadgeCheck
                              className="h-4 w-4 shrink-0 text-sky-500"
                              aria-label="Verified"
                            />
                          )}
                        </div>
                        <p className="max-w-[200px] truncate text-xs text-muted-foreground">
                          {player.user_email}
                        </p>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <TeamBadge name={player.team || 'No team'} colorCode={player.team_color_code} />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <Badge
                      variant={player.is_active ? 'default' : 'secondary'}
                      className={cn(
                        'text-xs font-medium whitespace-nowrap',
                        player.is_active &&
                          'bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20 border-0'
                      )}
                    >
                      {player.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0 opacity-70" />
                      {getTimeAgo(player.last_active)}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground tabular-nums">
                    {formatJoined(player.date_joined)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    <Link to={`/players/${player.user_id}/edit`}>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border/80 bg-background px-2.5 py-1.5 text-xs font-medium text-muted-foreground shadow-sm transition-colors hover:border-primary/30 hover:bg-muted/40 hover:text-foreground"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
      </Table>
      <div className="flex flex-col gap-4 border-t border-border bg-muted/25 px-5 py-4 sm:flex-row sm:items-center sm:justify-between dark:bg-muted/15">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-medium">{data.length}</span>
          {shownTotal > data.length ? (
            <>
              {' '}
              of <span className="font-medium">{shownTotal}</span>
            </>
          ) : null}{' '}
          players
        </p>
        {totalPages > 1 ? (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onNext={onNext}
            onPrev={onPrev}
            onFirst={onFirst}
            onLast={onLast}
          />
        ) : null}
      </div>
    </div>
  );
}
